// Canvas grab bar: a subtle horizontal bar that sits just below the cube
// floor (or above the ceiling, depending on the user's vantage). Aiming
// the laser at it and pulling the trigger drags the whole canvas in
// 3-space. This module owns the static geometry; the per-frame perch
// logic (`updateGrabBar`) lives in main.ts because it depends on
// controllers, the active interaction, and the user's head position.

import * as THREE from "three";

export const GRAB_BAR_WIDTH = 0.75;
// Slightly thinner than the cursor's 0.08m diameter so the cursor reads as
// "over" the bar rather than crowded against it.
const GRAB_BAR_HEIGHT = 0.06;
const GRAB_BAR_CORNER_RADIUS = 0.03;
export const GRAB_BAR_FLOOR_GAP = 0.1;
// Just a touch higher than the floor-perches' offset so the bar reads as
// floating clearly above the ceiling rather than nestled against it.
export const GRAB_BAR_CEILING_GAP = 0.4;
export const GRAB_BAR_STANDOFF = 0.3;

// Bar lives under a group whose rotation.y tracks the user-facing side of
// the cube. updateGrabBar owns position/scale/rotation each frame; the
// initial values here are placeholders for the first render before that
// runs.
export const grabBarGroup = new THREE.Group();

const grabBarMat = new THREE.MeshBasicMaterial({
  color: 0xf5f3ec, // soft off-white
  transparent: true,
  opacity: 0.92,
  depthWrite: false,
  side: THREE.DoubleSide,
});

// Flat, skinny rounded-corner rectangle in the XY plane. ShapeGeometry's
// default normal is +Z, which (after grabBarGroup's user-tracking yaw)
// points toward the user — i.e. the flat face faces them, like a window's
// title bar. The shape's outline runs counter-clockwise so the front face
// is visible; DoubleSide on the material covers the back if the user goes
// around.
function makeGrabBarShape(): THREE.Shape {
  const s = new THREE.Shape();
  const w = GRAB_BAR_WIDTH / 2;
  const h = GRAB_BAR_HEIGHT / 2;
  const r = Math.min(GRAB_BAR_CORNER_RADIUS, w, h);
  s.moveTo(-w + r, -h);
  s.lineTo(w - r, -h);
  s.quadraticCurveTo(w, -h, w, -h + r);
  s.lineTo(w, h - r);
  s.quadraticCurveTo(w, h, w - r, h);
  s.lineTo(-w + r, h);
  s.quadraticCurveTo(-w, h, -w, h - r);
  s.lineTo(-w, -h + r);
  s.quadraticCurveTo(-w, -h, -w + r, -h);
  return s;
}

export const grabBarMesh = new THREE.Mesh(
  new THREE.ShapeGeometry(makeGrabBarShape()),
  grabBarMat,
);
grabBarMesh.renderOrder = -1;
grabBarGroup.add(grabBarMesh);

// Black outline around the same rounded-rect path — sampled along the
// shape's curves so it traces the same rounded corners as the fill.
const grabBarOutlinePoints = makeGrabBarShape().getPoints(20);
const grabBarOutlineGeo = new THREE.BufferGeometry().setFromPoints(
  grabBarOutlinePoints,
);
const grabBarOutline = new THREE.LineLoop(
  grabBarOutlineGeo,
  new THREE.LineBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  }),
);
// Sit a hair in front of the fill so the line wins the z-fight on the bar
// front face. Both share grabBarGroup so they yaw/scale together.
grabBarOutline.position.z = 0.001;
grabBarMesh.add(grabBarOutline);

// Invisible hit pad — larger than the visible bar so the user doesn't have
// to thread a 3°-tall target with their controller. Same shape construction
// with a taller half-height + a wider margin so the click area encloses
// the bar generously. Parented to grabBarMesh so it inherits the lookAt
// tilt; material is invisible (opacity 0) but the geometry still raycasts.
const GRAB_BAR_HIT_HEIGHT = 0.22;
const GRAB_BAR_HIT_WIDTH = GRAB_BAR_WIDTH + 0.1;
function makeGrabBarHitShape(): THREE.Shape {
  const s = new THREE.Shape();
  const w = GRAB_BAR_HIT_WIDTH / 2;
  const h = GRAB_BAR_HIT_HEIGHT / 2;
  const r = Math.min(GRAB_BAR_CORNER_RADIUS, w, h);
  s.moveTo(-w + r, -h);
  s.lineTo(w - r, -h);
  s.quadraticCurveTo(w, -h, w, -h + r);
  s.lineTo(w, h - r);
  s.quadraticCurveTo(w, h, w - r, h);
  s.lineTo(-w + r, h);
  s.quadraticCurveTo(-w, h, -w, h - r);
  s.lineTo(-w, -h + r);
  s.quadraticCurveTo(-w, -h, -w + r, -h);
  return s;
}
const grabBarHitPad = new THREE.Mesh(
  new THREE.ShapeGeometry(makeGrabBarHitShape()),
  new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide,
  }),
);
grabBarMesh.add(grabBarHitPad);
// The hit pad is the raycast target rather than the visible mesh, so the
// effective click area is the padded box.
export const grabBarTargetMesh = grabBarHitPad;

// ── Exit Immersive Button ───────────────────────────────────────────────────
// A small circle to the LEFT of the grab bar with the same off-white fill
// and black border, plus an "X" stroked through it. Trigger-click ends the
// active XR session (handler in main.ts). Parented to grabBarMesh so it
// inherits the bar's position, yaw, scale, and lookAt-tilt — so it stays
// visually attached to the bar's left edge and the same face faces the user.
// Fixed in absolute world units so tweaking GRAB_BAR_HEIGHT doesn't pull
// the X button's size along with it.
const EXIT_BTN_RADIUS = 0.0385;
const EXIT_BTN_GAP = 0.18; // horizontal gap between bar's left edge and the circle
// Hover scale target shared with main.ts. The hit pad below is sized to
// match the enlarged button so hover triggers as soon as the cursor
// enters the would-be enlarged outline — even when the visible button
// is still at its small base size.
export const EXIT_BTN_HOVER_SCALE = 4.0;
export const exitButtonMesh = new THREE.Mesh(
  new THREE.CircleGeometry(EXIT_BTN_RADIUS, 32),
  grabBarMat,
);
exitButtonMesh.position.set(
  -(GRAB_BAR_WIDTH / 2 + EXIT_BTN_GAP + EXIT_BTN_RADIUS),
  0,
  0,
);
exitButtonMesh.renderOrder = -1;
grabBarMesh.add(exitButtonMesh);

// Invisible hit pad — sized to the FULL hover radius plus the off-
// canvas cursor's own radius, so hover triggers when the cursor's EDGE
// crosses the enlarged button's edge rather than when its CENTER does.
// Padding is derived from the cursor's apparent-size ratio divided by
// the bar's: cursor_world_radius / bar_cumulative_scale simplifies to
// (OFF_CANVAS_CURSOR_APPARENT_RATIO × GRAB_BAR_WIDTH) / GRAB_BAR_APPARENT_RATIO
// ≈ (0.0075 × 0.75) / 0.1 = 0.056 in bar-local units. Re-derive if any
// of those three constants change (they live in main.ts and
// controller-ray.ts respectively).
const EXIT_BTN_HIT_CURSOR_PAD = 0.056;
// Parented to grabBarMesh as a sibling of exitButtonMesh so it inherits
// the bar's transforms but NOT the button's hover-scale lerp.
export const exitButtonHitPad = new THREE.Mesh(
  new THREE.CircleGeometry(
    EXIT_BTN_RADIUS * EXIT_BTN_HOVER_SCALE + EXIT_BTN_HIT_CURSOR_PAD,
    32,
  ),
  new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide,
  }),
);
exitButtonHitPad.position.copy(exitButtonMesh.position);
grabBarMesh.add(exitButtonHitPad);
// Border for the circle — sample a circle and feed into LineLoop.
const exitBorderPts: THREE.Vector3[] = [];
const EXIT_BORDER_SEG = 32;
for (let s = 0; s < EXIT_BORDER_SEG; s++) {
  const a = (s / EXIT_BORDER_SEG) * Math.PI * 2;
  exitBorderPts.push(
    new THREE.Vector3(
      Math.cos(a) * EXIT_BTN_RADIUS,
      Math.sin(a) * EXIT_BTN_RADIUS,
      0,
    ),
  );
}
const exitButtonBorder = new THREE.LineLoop(
  new THREE.BufferGeometry().setFromPoints(exitBorderPts),
  new THREE.LineBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  }),
);
exitButtonBorder.position.z = 0.001;
exitButtonMesh.add(exitButtonBorder);
// X icon: two crossed line segments. Drawn slightly in front of the fill
// for the same z-fight reason as the bar's outline.
const exitXR = EXIT_BTN_RADIUS * 0.38;
export const exitXMesh = new THREE.LineSegments(
  new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-exitXR, -exitXR, 0),
    new THREE.Vector3(exitXR, exitXR, 0),
    new THREE.Vector3(-exitXR, exitXR, 0),
    new THREE.Vector3(exitXR, -exitXR, 0),
  ]),
  new THREE.LineBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  }),
);
exitXMesh.position.z = 0.0015;
// X glyph is hidden by default; main.ts toggles it visible on hover.
exitXMesh.visible = false;
exitButtonMesh.add(exitXMesh);
